import { Entity, Quat, Vec3 } from 'playcanvas';

import { CubicSpline } from './anim/spline';
import { AnimTrack, KeyEasing } from './anim-track';
import { Events } from './events';

type SerializedElementPose = {
    frame: number;
    position: [number, number, number];
    rotation: [number, number, number, number];
    scale: [number, number, number];
    easing?: KeyEasing;
};

type ElementPose = {
    frame: number;
    position: Vec3;
    rotation: Quat;
    scale: Vec3;
    easing?: KeyEasing;
};

type AnimatableTarget = {
    animationControllerEntity: Entity;
    move?: (position?: Vec3, rotation?: Quat, scale?: Vec3) => void;
};

const remapEasing = (t: number, easing: KeyEasing | undefined) => {
    if (!easing) return t;
    switch (easing) {
        case 'linear': return t;
        case 'easeIn': return t * t;
        case 'easeOut': return 1 - (1 - t) * (1 - t);
        case 'easeInOut': return t * t * (3 - 2 * t);
        case 'step': return t < 1 ? 0 : 1;
        default: return t;
    }
};

const wrapTime = (time: number, duration: number) => {
    if (!(duration > 0)) {
        return time;
    }
    const wrapped = time % duration;
    return wrapped < 0 ? wrapped + duration : wrapped;
};

const clonePose = (pose: ElementPose): ElementPose => ({
    frame: pose.frame,
    position: pose.position.clone(),
    rotation: pose.rotation.clone(),
    scale: pose.scale.clone(),
    easing: pose.easing
});

const serializePose = (pose: ElementPose): SerializedElementPose => ({
    frame: pose.frame,
    position: [pose.position.x, pose.position.y, pose.position.z],
    rotation: [pose.rotation.x, pose.rotation.y, pose.rotation.z, pose.rotation.w],
    scale: [pose.scale.x, pose.scale.y, pose.scale.z],
    easing: pose.easing
});

const deserializePose = (pose: SerializedElementPose): ElementPose => ({
    frame: pose.frame,
    position: new Vec3(pose.position[0], pose.position[1], pose.position[2]),
    rotation: new Quat(pose.rotation[0], pose.rotation[1], pose.rotation[2], pose.rotation[3]),
    scale: new Vec3(pose.scale[0], pose.scale[1], pose.scale[2]),
    easing: pose.easing
});

class ElementAnimTrack implements AnimTrack {
    private poses: ElementPose[] = [];
    private readonly events: Events;
    private readonly target: AnimatableTarget;
    private readonly controllerRoot: Entity;
    private orderedPoses: ElementPose[] = [];
    private positionSpline: CubicSpline | null = null;
    private scaleSpline: CubicSpline | null = null;
    private duration = 0;

    constructor(events: Events, target: AnimatableTarget) {
        this.events = events;
        this.target = target;
        this.controllerRoot = target.animationControllerEntity;
    }

    get keys(): readonly number[] {
        return this.poses.map((pose) => pose.frame);
    }

    addKey(frame: number): boolean {
        const existingIndex = this.poses.findIndex((pose) => pose.frame === frame);
        const existing = existingIndex === -1 ? undefined : this.poses[existingIndex];
        const nextPose = this.capturePose(frame, existing?.easing);

        if (existingIndex === -1) {
            this.poses.push(nextPose);
            this.rebuild();
            this.events.fire('track.keyAdded', frame);
        } else {
            this.poses[existingIndex] = nextPose;
            this.rebuild();
            this.events.fire('track.keyUpdated', frame);
        }

        return true;
    }

    removeKey(frame: number): boolean {
        const index = this.poses.findIndex((pose) => pose.frame === frame);
        if (index === -1) {
            return false;
        }

        this.poses.splice(index, 1);
        this.rebuild();
        this.events.fire('track.keyRemoved', frame);
        return true;
    }

    moveKey(fromFrame: number, toFrame: number): boolean {
        if (fromFrame === toFrame) {
            return false;
        }

        const fromIndex = this.poses.findIndex((pose) => pose.frame === fromFrame);
        if (fromIndex === -1) {
            return false;
        }

        const toIndex = this.poses.findIndex((pose) => pose.frame === toFrame);
        if (toIndex !== -1) {
            this.poses.splice(toIndex, 1);
        }

        const movedIndex = this.poses.findIndex((pose) => pose.frame === fromFrame);
        this.poses[movedIndex].frame = toFrame;
        this.rebuild();
        this.events.fire('track.keyMoved', fromFrame, toFrame);
        return true;
    }

    copyKey(fromFrame: number, toFrame: number): boolean {
        if (fromFrame === toFrame) {
            return false;
        }

        const source = this.poses.find((pose) => pose.frame === fromFrame);
        if (!source) {
            return false;
        }

        const toIndex = this.poses.findIndex((pose) => pose.frame === toFrame);
        if (toIndex !== -1) {
            this.poses.splice(toIndex, 1);
        }

        this.poses.push({
            frame: toFrame,
            position: source.position.clone(),
            rotation: source.rotation.clone(),
            scale: source.scale.clone(),
            easing: source.easing
        });
        this.rebuild();
        this.events.fire('track.keyAdded', toFrame);
        return true;
    }

    getKeyEasing(frame: number): KeyEasing | undefined {
        return this.poses.find((pose) => pose.frame === frame)?.easing;
    }

    setKeyEasing(frame: number, easing: KeyEasing | undefined): boolean {
        const pose = this.poses.find((entry) => entry.frame === frame);
        if (!pose || pose.easing === easing) {
            return false;
        }

        pose.easing = easing;
        this.rebuild();
        this.events.fire('track.keyUpdated', frame);
        return true;
    }

    clear(): void {
        this.poses.length = 0;
        this.orderedPoses = [];
        this.positionSpline = null;
        this.scaleSpline = null;
        this.events.fire('track.keysCleared');
    }

    snapshot(): ElementPose[] {
        return this.poses.map(clonePose);
    }

    getPoses(): SerializedElementPose[] {
        return this.poses.map(serializePose);
    }

    restore(snapshot: unknown): void {
        const poses = Array.isArray(snapshot) ? snapshot as ElementPose[] : [];
        this.poses = poses.map(clonePose);
        this.rebuild();
        this.events.fire('track.keysLoaded');
    }

    loadPoses(poses: SerializedElementPose[] | null | undefined): void {
        this.poses = Array.isArray(poses) ? poses.map(deserializePose) : [];
        this.rebuild();
        this.events.fire('track.keysLoaded');
    }

    evaluate(frame: number): boolean {
        const pose = this.sample(frame);
        if (!pose) {
            return false;
        }

        if (typeof this.target.move === 'function') {
            this.target.move(pose.position, pose.rotation, pose.scale);
        } else {
            this.controllerRoot.setLocalPosition(pose.position);
            this.controllerRoot.setLocalRotation(pose.rotation);
            this.controllerRoot.setLocalScale(pose.scale);
        }
        return true;
    }

    rebuild(): void {
        const timelineFrames = this.events.invoke('timeline.frames');
        const smoothness = this.events.invoke('timeline.smoothness');
        const safeSmoothness = (typeof smoothness === 'number' && Number.isFinite(smoothness)) ? smoothness : 1;

        this.orderedPoses = this.poses.slice().sort((a, b) => a.frame - b.frame);
        const maxFrame = this.orderedPoses.length > 0 ? this.orderedPoses[this.orderedPoses.length - 1].frame + 1 : 1;
        this.duration = (typeof timelineFrames === 'number' && Number.isFinite(timelineFrames) && timelineFrames > 0) ? timelineFrames : maxFrame;
        this.orderedPoses = this.orderedPoses.filter((pose) => pose.frame < this.duration);

        if (this.orderedPoses.length > 1) {
            const times = this.orderedPoses.map((pose) => pose.frame);
            const easings = this.orderedPoses.map((pose) => pose.easing);
            const positions: number[] = [];
            const scales: number[] = [];

            this.orderedPoses.forEach((pose) => {
                positions.push(pose.position.x, pose.position.y, pose.position.z);
                scales.push(pose.scale.x, pose.scale.y, pose.scale.z);
            });

            this.positionSpline = CubicSpline.fromPointsLooping(this.duration, times, positions, safeSmoothness, easings);
            this.scaleSpline = CubicSpline.fromPointsLooping(this.duration, times, scales, safeSmoothness, easings);
        } else {
            this.positionSpline = null;
            this.scaleSpline = null;
        }
    }

    private capturePose(frame: number, easing?: KeyEasing): ElementPose {
        return {
            frame,
            position: this.controllerRoot.getLocalPosition().clone(),
            rotation: this.controllerRoot.getLocalRotation().clone(),
            scale: this.controllerRoot.getLocalScale().clone(),
            easing
        };
    }

    private sample(frame: number): ElementPose | null {
        if (this.orderedPoses.length === 0) {
            return null;
        }

        if (this.orderedPoses.length === 1) {
            return clonePose(this.orderedPoses[0]);
        }

        const sampleTime = wrapTime(frame, this.duration);
        const position = new Vec3();
        const scale = new Vec3();
        const rotation = new Quat();
        const positionValues: number[] = [];
        const scaleValues: number[] = [];

        this.positionSpline?.evaluate(sampleTime, positionValues);
        this.scaleSpline?.evaluate(sampleTime, scaleValues);

        position.set(positionValues[0], positionValues[1], positionValues[2]);
        scale.set(scaleValues[0], scaleValues[1], scaleValues[2]);
        this.sampleRotation(sampleTime, rotation);

        return {
            frame: sampleTime,
            position,
            rotation,
            scale
        };
    }

    private sampleRotation(frame: number, out: Quat) {
        const poses = this.orderedPoses;
        const count = poses.length;

        if (count === 0) {
            out.set(0, 0, 0, 1);
            return;
        }

        if (count === 1) {
            out.copy(poses[0].rotation);
            return;
        }

        for (let i = 0; i < count; i++) {
            const from = poses[i];
            const to = poses[(i + 1) % count];
            const start = from.frame;
            const end = i === count - 1 ? to.frame + this.duration : to.frame;
            const segmentFrame = (i === count - 1 && frame < start) ? frame + this.duration : frame;

            if (segmentFrame < start || segmentFrame >= end) {
                continue;
            }

            const span = end - start;
            const alpha = span > 0 ? (segmentFrame - start) / span : 0;
            out.slerp(from.rotation, to.rotation, remapEasing(alpha, from.easing)).normalize();
            return;
        }

        out.copy(poses[count - 1].rotation);
    }
}

export { ElementAnimTrack };
export type { ElementPose, SerializedElementPose };